const { resolveRequestSchema } = require('./resolveRequestSchema');

const VALUE_CONSTRAINTS = [
    'type',
    'enum',
    'const',
    'default',
    'minLength',
    'maxLength',
    'pattern',
    'format',
    'minimum',
    'maximum',
    'exclusiveMinimum',
    'exclusiveMaximum',
    'multipleOf',
    'minItems',
    'maxItems',
    'uniqueItems'
];

/**
 * Hidden custom APIs own their request contracts. Their UI forms can still declare mandatory
 * inputs, but display fields and omitted protocol fields do not define the rest of the payload.
 */
function requiredPayloadSchema(requestSchema, schemas) {
    const base = resolveRequestSchema(requestSchema, schemas);
    const required = [...new Set([...(base.required || []), ...(requestSchema.required || [])])];
    const alternatives = requestSchema.anyOf?.map(branch =>
        requiredPayloadSchema(
            {
                ...branch,
                properties: { ...base.properties, ...branch.properties }
            },
            schemas
        )
    );
    const result = {
        type: 'object',
        properties: Object.fromEntries(
            required.map(name => [name, valueConstraints(base.properties?.[name] || {}, schemas)])
        ),
        ...(required.length ? { required } : {}),
        ...(alternatives ? { anyOf: alternatives } : {}),
        additionalProperties: true
    };
    const defaults = defaultStages(alternatives || []);
    return defaults.length ? { type: 'object', allOf: [...defaults, result] } : result;
}

/** Preserve full validation without letting merged optional defaults choose a different form. */
function fullPayloadSchema(requestSchema, schemas) {
    const base = resolveRequestSchema(requestSchema, schemas);
    const full = withoutOptionalDefaults(base, schemas, new Set(base.required || []));
    const required = requiredPayloadSchema(requestSchema, schemas);
    const defaults = required.allOf?.slice(0, -1) || [];
    if (!full.changed && !defaults.length) return requestSchema;
    return defaults.length ? { type: 'object', allOf: [...defaults, full.schema] } : full.schema;
}

function withoutOptionalDefaults(schema, schemas, required) {
    const result = { ...schema };
    let changed = false;
    const properties = { ...schema.properties };
    for (const [name, property] of Object.entries(properties)) {
        if (required.has(name)) continue;
        const value = resolveRequestSchema(property, schemas);
        if (value.default === undefined) continue;
        properties[name] = { ...value };
        delete properties[name].default;
        changed = true;
    }
    if (changed) result.properties = properties;
    for (const keyword of ['anyOf', 'oneOf', 'allOf']) {
        if (!schema[keyword]) continue;
        const branches = schema[keyword].map(branch => withoutOptionalDefaults(branch, schemas, required));
        if (branches.some(branch => branch.changed)) {
            result[keyword] = branches.map(branch => branch.schema);
            changed = true;
        }
    }
    return { schema: result, changed };
}

function defaultStages(alternatives) {
    const matchesWithoutDefaults = form => ({
        ...form,
        required: (form.required || []).filter(name => form.properties?.[name]?.default === undefined)
    });
    return alternatives.flatMap((form, index) => {
        const properties = Object.fromEntries(
            (form.required || []).flatMap(name => {
                const value = form.properties?.[name]?.default;
                return value !== undefined ? [[name, { default: value }]] : [];
            })
        );
        if (!Object.keys(properties).length) return [];
        // AJV ignores defaults inside anyOf. Apply them before validation, only for matching forms.
        return [
            {
                if: {
                    allOf: [
                        matchesWithoutDefaults(form),
                        ...alternatives
                            .filter((_, other) => other !== index)
                            .map(other => ({
                                not: matchesWithoutDefaults(other)
                            }))
                    ]
                },
                then: { properties }
            }
        ];
    });
}

function valueConstraints(schema, schemas, ancestors = new Set()) {
    const marker = schema.$ref || schema;
    if (ancestors.has(marker)) throw new Error('Circular required value schema');
    const nextAncestors = new Set(ancestors).add(marker);
    const value = resolveRequestSchema(schema, schemas);
    // A selected reference is an object, not a new instance of the displayed record. Its nested
    // fields are validated by the handler, which also accepts shapes absent from the form.
    if (value.type === 'object' || value.properties) {
        return { type: value.type || 'object', ...(value.default !== undefined ? { default: value.default } : {}) };
    }
    const result = Object.fromEntries(
        VALUE_CONSTRAINTS.filter(key => value[key] !== undefined).map(key => [key, value[key]])
    );
    if (value.items) result.items = valueConstraints(value.items, schemas, nextAncestors);
    for (const key of ['anyOf', 'oneOf', 'allOf']) {
        if (value[key]) result[key] = value[key].map(branch => valueConstraints(branch, schemas, nextAncestors));
    }
    return result;
}

module.exports = { requiredPayloadSchema, fullPayloadSchema };
